import React, { useState } from "react";
import Drawer from "@mui/material/Drawer";
import { ArrowLeftIcon, PlusIcon, TrashIcon } from "@heroicons/react/solid";
import useReadWithGet from "../../hooks/read/useReadWithGetMethod";
import { useParams } from "react-router-dom";
import Moment from "react-moment";
import usePut from "../../hooks/usePut";
import toast, { Toaster } from "react-hot-toast";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import Pagination from "../../components/pagination";
import useRead from "../../hooks/useRead";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { Button } from "../../components/ui";
import RoundedContainer from "../../components/containers/RoundedContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

const EmploymentTypeModal = ({ open, onClose, mode }) => {
  const { company_employee_id, username } = useParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEmploymentToDelete, setSelectedEmploymentToDelete] =
    useState(null);

  const { list } = useRead({
    url: "employment_listing",
    initialData: {
      limit: 50,
      searchQuery: "",
    },
  });
  const {
    list: userEmploymentListing,
    paramsObject,
    setGetListParams,
  } = useReadWithGet({
    url: "user_employment_type_listing",
    initialData: {
      limit: 100,
      searchQuery: "",
      company_employee_id: company_employee_id,
      page_number: 0,
    },
  });
  const { handleAuth: submitEmploymentType } = usePut({
    url: "user_employment_type_update",
    refreshUrl: "user_employment_type_listing",
    onSuccess: () => {
      toast.success("Employee Employment Type Updated");
      onClose();
    },
    onError: () => {
      toast.error(
        "There was error while updating employment type please try again later"
      );
    },
  });
  const { handleAuth: deleteEmploymentType } = usePut({
    url: "delete_user_employment_mapping",
    refreshUrl: "user_employment_type_listing",
    onSuccess: () => {
      toast.success("Employee Employment Type Deleted Successfully");
      setIsDeleteModalOpen(false);
      setSelectedEmploymentToDelete(null);
    },
    onError: () => {
      toast.error(
        "There was an error while deleting employment type, please try again later"
      );
      setIsDeleteModalOpen(false);
    },
  });

  const [employmentState, setEmploymentState] = useState({
    employment_type_id: "",
    company_employee_id: company_employee_id,
    effect_from: "",
  });
  const handleChangeEmployment = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    setEmploymentState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  const handleUpdateEmployment = () => {
    const { employment_type_id, effect_from } = employmentState;
    if (!employment_type_id || !effect_from) {
      return alert("Please Enter Employment Type and Effect From");
    }
    submitEmploymentType.mutate(employmentState);
  };
  const handlePageClick = (val) => {
    let data = {
      ...paramsObject,
      page_number: val.selected,
    };
    setGetListParams(data);
  };

  const handleDeleteClick = (employment) => {
    setSelectedEmploymentToDelete(employment);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedEmploymentToDelete) {
      deleteEmploymentType.mutate({
        user_emp_type_map_id: selectedEmploymentToDelete.user_emp_type_map_id,
      });
    }
  };

  return (
    <>
      <Drawer anchor={"right"} open={open} onClose={onClose}>
        {(submitEmploymentType.isLoading || deleteEmploymentType.isLoading) && (
          <div className=" fixed w-full h-full top-0 left-0 flex items-center justify-center bg-[#ffffffab]">
            <PrimaryLoader />
          </div>
        )}
        <div
          className="h-full bg-white p-8"
          style={{ width: "calc(100vw - 600px)" }}
        >
          <form className="space-y-8">
            <div>
              <div className="flex items-center justify-between space-x-3 pb-5 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <ArrowLeftIcon
                    className="text-gray-700 w-8 h-8 cursor-pointer"
                    onClick={onClose}
                  />
                  <p className="text-gray-700 text-xl font-semibold">
                    Employment Type details for {username}
                  </p>
                </div>
                <div>
                  {mode !== "resigned" && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={handleUpdateEmployment}
                    >
                      Update Employment Type
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-x-5 py-5">
                <div>
                  <div className="">
                    <label className="label">Current Employment Type: </label>
                    <span className="block font-semibold text-base bg-red-100 text-red-600 py-2 rounded-md text-center">
                      {Array.isArray(
                        userEmploymentListing?.data?.current_employment_data
                      )
                        ? "No employment type assigned"
                        : userEmploymentListing?.data?.current_employment_data
                            ?.employment_name || "No employment type assigned"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="">
                    <label className="label">New Employment Type: </label>
                    <select
                      disabled={mode === "resigned"}
                      name="employment_type_id"
                      onChange={handleChangeEmployment}
                      className="input"
                    >
                      <option value={employmentState.employment_type_id}>
                        --Select Employment Type--
                      </option>
                      {list.data?.data?.map((employmentData) => {
                        return (
                          <option
                            key={employmentData.id}
                            value={employmentData.id}
                          >
                            {employmentData.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div>
                  <div>
                    <label className="label">Effect From </label>
                    <input
                      disabled={mode === "resigned"}
                      value={employmentState.effect_from}
                      onChange={handleChangeEmployment}
                      type="date"
                      name="effect_from"
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-gray-700 text-3xl font-semibold mb-4">
                  Previous Assigned Employment Types
                </p>
              </div>
              {!userEmploymentListing.data?.data ||
              userEmploymentListing.data.data.length === 0 ? (
                <p className="text-gray-500 italic text-center py-4">
                  No previous employment types found
                </p>
              ) : (
                <>
                  <div className="max-w-full overflow-x-auto mt-4">
                    <Table className="table-auto w-full">
                      <TableHead>
                        <TableHeaderCell className="w-1/3 text-center">
                          Employment Type
                        </TableHeaderCell>
                        <TableHeaderCell className="w-1/3 text-center">
                          Effected From
                        </TableHeaderCell>
                        <TableHeaderCell className="w-1/3 text-center">
                          Effected Till
                        </TableHeaderCell>
                        {/* <TableHeaderCell className="w-[10%] text-center">Actions</TableHeaderCell> */}
                      </TableHead>
                      <TableBody>
                        {userEmploymentListing.data?.data.map((item) => {
                          return (
                            <TableRow key={item.user_emp_type_map_id}>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.employment_name || "----"}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.from_date ? (
                                  <Moment
                                    unix
                                    date={item.from_date}
                                    format="DD/MM/YYYY"
                                  />
                                ) : (
                                  "----"
                                )}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.to_date ? (
                                  <Moment
                                    unix
                                    date={item.to_date}
                                    format="DD/MM/YYYY"
                                  />
                                ) : (
                                  "----"
                                )}
                              </TableCell>
                              {/* <TableCell className="text-center">
                                <Button 
                                  type="button" 
                                  variant={"ghost"}
                                  size={"icon"}
                                  onClick={() => handleDeleteClick(item)} 
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <TrashIcon className="w-5 h-5 mx-auto" />
                                </Button>
                              </TableCell> */}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="px-5 mt-5">
                    <Pagination
                      currentPage={+paramsObject.page_number}
                      lengthofItems={userEmploymentListing.data?.total_count}
                      limit={paramsObject.limit}
                      onPageChange={handlePageClick}
                    />
                  </div>
                </>
              )}
            </div>
          </form>
        </div>
      </Drawer>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Employment Type Assignment"
        description={
          selectedEmploymentToDelete ? (
            <>
              Are you sure you want to delete the{" "}
              <span className="font-bold text-red-600">
                {selectedEmploymentToDelete.employment_name}
              </span>{" "}
              employment type assignment? This action cannot be undone.
            </>
          ) : (
            "Are you sure you want to delete this employment type assignment? This action cannot be undone."
          )
        }
        confirmButtonLabel="Delete"
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default EmploymentTypeModal;
